import dayjs from 'dayjs';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'shared/hooks/useClickOutside';
import { AngleIcon } from 'shared/icons';
import { cn } from 'shared/utils/cn';

export const LanguageSwitch: FC<{ className?: string }> = ({ className }) => {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useClickOutside(wrapperRef, () => setOpen(false));
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open]);
  const language = i18n.resolvedLanguage?.split('-')[0] ?? 'pl';
  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-label={t('common:language')}
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1.5 rounded px-2 py-2 text-xs font-medium text-black/80 hover:bg-black/5"
      >
        <span className="uppercase">{language}</span>
        <AngleIcon className={cn('h-3 w-3', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="shadow-paper absolute top-full right-0 z-40 mt-2 min-w-40 rounded-lg border border-black/10 bg-white p-1">
          {(['pl', 'en'] as const).map((value) => (
            <button
              key={value}
              type="button"
              disabled={value === language}
              onClick={async () => {
                await i18n.changeLanguage(value);
                dayjs.locale(value);
                setOpen(false);
                buttonRef.current?.focus();
              }}
              className="disabled:bg-primary/10 disabled:text-primary block w-full rounded px-3 py-2 text-left text-sm text-black/80 hover:bg-gray-200"
            >
              {t(`dictionaries:languages.${value}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
